import { z } from 'zod';
import { PasswordSchema } from './auth.js';
export const OnboardingModeSchema = z.enum(['bootstrap', 'setup', 'normal', 'licensing']);
/** 라이선스 슬롯 1개의 admin/status 응답용 요약 (token 본문은 제외). */
export const LicenseSlotSummarySchema = z.object({
    id: z.string().uuid(),
    installedAt: z.string().datetime(),
    activatedAt: z.string().datetime().nullable(),
    retiredAt: z.string().datetime().nullable(),
    expiresAt: z.string().datetime().nullable(),
    features: z.array(z.string()).nullable(),
    hwid: z.string().nullable(),
    retiredReason: z.string().nullable(),
});
/** GET /api/license/status 응답. */
export const LicenseStatusSchema = z.object({
    enforced: z.boolean(),
    hasToken: z.boolean(),
    hwid: z.string().nullable(),
    valid: z.boolean(),
    features: z.array(z.string()),
    expiresAt: z.string().datetime().nullable(),
    reason: z.string().nullable(),
    warning: z.string().nullable().optional(),
    // N슬롯 모델 — 현재 활성 + 대기 큐.
    active: LicenseSlotSummarySchema.nullable(),
    pending: z.array(LicenseSlotSummarySchema),
    autoSwapEnabled: z.boolean(),
});
/** POST /api/license/challenge 요청·응답. */
export const LicenseChallengeRequestSchema = z.object({
    licenseKey: z.string().min(1).max(200),
});
export const LicenseChallengeResponseSchema = z.object({
    challenge: z.string(),
    hwid: z.string(),
});
/** POST /api/license/install 요청·응답. */
export const LicenseInstallRequestSchema = z.object({
    token: z.string().min(1).max(8192),
});
export const LicenseInstallResponseSchema = z.object({
    ok: z.literal(true),
    features: z.array(z.string()),
    expiresAt: z.string().datetime().nullable(),
    mode: OnboardingModeSchema,
    /**
     * 'active' = 첫 라이선스 (또는 active 슬롯이 비어있던 시점) — 즉시 활성화.
     * 'pending' = 이미 active 가 있어 다음 라이선스 큐에 등록. 현재 라이선스 만료 시 자동 swap.
     */
    slot: z.enum(['active', 'pending']),
});
/** POST /api/license/slots/:id/activate 및 /retire 공통 응답. */
export const LicenseSlotActionResponseSchema = z.object({
    ok: z.literal(true),
    mode: OnboardingModeSchema,
});
export const OnboardingStepSchema = z.enum(['db', 'admin', 'llm', 'storage', 'done']);
export const OnboardingStatusSchema = z.object({
    mode: OnboardingModeSchema,
    step: OnboardingStepSchema,
    keyMismatch: z.boolean().optional(),
});
export const OnboardingDbResponseSchema = z.object({
    ok: z.literal(true),
    existingDatabase: z.boolean().optional(),
});
export const OnboardingDbRequestSchema = z.object({
    /**
     * 시스템 DB driver. 미설정 시 'postgres' (기존 클라이언트 호환). Oracle 선택 시
     * excelConnect(엑셀 격리 접속) 필수 — [[excel-isolation-decision]].
     */
    driver: z.enum(['postgres', 'oracle']).default('postgres'),
    host: z.string().min(1),
    port: z.coerce.number().int().min(1).max(65535).default(4235),
    database: z.string().min(1),
    username: z.string().min(1),
    password: z.string().min(1),
    /** Oracle 전용 접속 옵션. driver='oracle' 일 때만 의미. */
    oracleConnect: z
        .object({
        serviceName: z.string().optional(),
        sid: z.string().optional(),
        thickMode: z.boolean().default(false),
        clientLibDir: z.string().optional(),
    })
        .optional(),
    /** 엑셀 첨부 격리 전용 별도 DB 접속. Oracle 필수 / PG 옵션. */
    excelConnect: z
        .object({
        host: z.string().min(1),
        port: z.coerce.number().int().min(1).max(65535),
        database: z.string().min(1),
        username: z.string().min(1),
        password: z.string().min(1),
    })
        .optional(),
});
export const StorageTypeSchema = z.enum(['s3', 'fs', 'nfs']);
/**
 * S3 호환 (MinIO / SeaweedFS / AWS S3) 입력. region / forcePathStyle 은 기본값 사용.
 *
 * tempCachePath: DuckDB 같이 로컬 파일 접근이 필요한 사본을 캐시할 디렉터리.
 * s3 type 일 때만 의미. 절대경로. null/빈문자열 이면 OS 기본 임시 디렉터리 사용.
 */
export const S3StorageConfigInputSchema = z.object({
    type: z.literal('s3'),
    endpoint: z.string().url(),
    accessKey: z.string().min(1),
    secretKey: z.string().min(1),
    bucket: z
        .string()
        .min(3)
        .max(63)
        .regex(/^[a-z0-9][a-z0-9.-]*[a-z0-9]$/, '소문자/숫자/.- 만 사용 (3~63자)'),
    region: z.string().min(1).max(64).default('us-east-1'),
    forcePathStyle: z.boolean().default(true),
    tempCachePath: z
        .string()
        .max(512)
        .regex(/^\//, '절대 경로(/) 로 시작해야 합니다')
        .nullable()
        .optional(),
});
/**
 * 파일시스템 기반 (Local FS / NFS). NFS 의 경우 OS 에서 미리 mount 한 마운트포인트를 입력.
 * 앱은 mount 명령을 실행하지 않는다.
 */
export const FsStorageConfigInputSchema = z.object({
    type: z.enum(['fs', 'nfs']),
    /** 절대경로 (마운트포인트 또는 로컬 디스크 디렉터리). */
    rootPath: z.string().min(1).max(512).regex(/^\//, '절대 경로(/) 로 시작해야 합니다'),
});
/**
 * 파일 저장소 입력 — 백엔드 종류별 discriminatedUnion. 온보딩 / 관리자 화면 공용.
 */
export const StorageConfigInputSchema = z.discriminatedUnion('type', [
    S3StorageConfigInputSchema,
    FsStorageConfigInputSchema,
]);
/** 온보딩 storage 단계: 건너뛰거나 설정 저장. */
export const OnboardingStorageRequestSchema = z.discriminatedUnion('action', [
    z.object({ action: z.literal('skip') }),
    z.object({ action: z.literal('save'), config: StorageConfigInputSchema }),
]);
export const OnboardingAdminEntrySchema = z.object({
    email: z.string().email(),
    displayName: z.string().min(1).max(80),
    password: PasswordSchema,
});
export const OnboardingAdminRequestSchema = z.object({
    admins: z.array(OnboardingAdminEntrySchema).min(1).max(10),
});
export const DEFAULT_ADMIN = {
    email: 'admin@innermate.local',
    displayName: '관리자',
    password: 'Innermate00!',
};
export const OnboardingLlmRequestSchema = z.object({
    name: z.string().min(1).max(80),
    baseUrl: z.string().url(),
    apiKey: z.string().min(1),
    model: z.string().min(1),
});
//# sourceMappingURL=onboarding.js.map